import Attendance, { AttendanceInput } from 'src/models/Attendance';
import AttendanceLogEntry from 'src/models/AttendanceLogEntry';
import DateOnly from 'src/models/common/DateOnly';
import api from './api';
import defaultErrorConverter from './errorHandling/defaultErrorConverter';

interface AttendanceRefParameters {
  classId: number;
  studentUserId: number;
}

export interface SaveAttendanceParameters extends AttendanceRefParameters {
  entries: AttendanceInput[];
}

export interface ApproveAttendanceParameters extends AttendanceRefParameters {
  attendanceId: number;
}

export interface AddAttendanceLogEntryParameters extends ApproveAttendanceParameters {
  text: string;
}

export interface UpdateAttendanceLogEntryParameters extends AddAttendanceLogEntryParameters {
  logEntryId: number;
}

export async function fetchAttendance({ classId, studentUserId }: AttendanceRefParameters): Promise<Attendance[]> {
  try {
    const res = await api.get(`api/classes/${classId}/students/${studentUserId}/attendance`);
    const entries = res.data.data as Attendance[];
    return entries.map(parseAttendance);
  } catch (error) {
    throw defaultErrorConverter(error);
  }
}

export async function saveAttendance({ classId, studentUserId, entries }: SaveAttendanceParameters): Promise<Attendance[]> {
  try {
    const res = await api.put(`api/classes/${classId}/students/${studentUserId}/attendance`, { data: entries });
    const saved = res.data.data as Attendance[];
    return saved.map(parseAttendance);
  } catch (error) {
    throw defaultErrorConverter(error);
  }
}

export async function approveAttendance({ classId, studentUserId, attendanceId }: ApproveAttendanceParameters): Promise<void> {
  try {
    await api.post(`api/classes/${classId}/students/${studentUserId}/attendance/${attendanceId}/approve`);
  } catch (error) {
    throw defaultErrorConverter(error);
  }
}

export async function unapproveAttendance({ classId, studentUserId, attendanceId }: ApproveAttendanceParameters): Promise<void> {
  try {
    await api.delete(`api/classes/${classId}/students/${studentUserId}/attendance/${attendanceId}/approve`);
  } catch (error) {
    throw defaultErrorConverter(error);
  }
}

export async function fetchAttendanceLog({ classId, studentUserId }: AttendanceRefParameters): Promise<AttendanceLogEntry[]> {
  try {
    const res = await api.get(`api/classes/${classId}/students/${studentUserId}/attendance/log`);
    return res.data.data as AttendanceLogEntry[];
  } catch (error) {
    throw defaultErrorConverter(error);
  }
}

export async function addAttendanceLogEntry({ classId, studentUserId, attendanceId, text }: AddAttendanceLogEntryParameters): Promise<AttendanceLogEntry> {
  try {
    const res = await api.post(`api/classes/${classId}/students/${studentUserId}/attendance/${attendanceId}/log`, { data: { text } });
    return res.data.data as AttendanceLogEntry;
  } catch (error) {
    throw defaultErrorConverter(error);
  }
}

export async function updateAttendanceLogEntry({ classId, studentUserId, attendanceId, logEntryId, text }: UpdateAttendanceLogEntryParameters): Promise<AttendanceLogEntry> {
  try {
    const res = await api.put(`api/classes/${classId}/students/${studentUserId}/attendance/${attendanceId}/log/${logEntryId}`, { data: { text } });
    return res.data.data as AttendanceLogEntry;
  } catch (error) {
    throw defaultErrorConverter(error);
  }
}

function parseAttendance(entry: Attendance): Attendance {
  return { ...entry, date: new DateOnly(entry.date) };
}
